import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useRealtimeTick } from "@/lib/realtime";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Megaphone, Power } from "lucide-react";
import { toast } from "sonner";
import { fmtNum } from "@/lib/helpers";

const emptyForm = {
  name: "", description: "", product_ids: [], scope: "branch",
  threshold_qty: 0, reward_amount: 0, reward_type: "fixed",
  tiers: [{ threshold_qty: 10, reward_amount: 500 }],
  start_date: new Date().toISOString().slice(0,10),
  end_date: new Date(new Date().setMonth(new Date().getMonth()+1)).toISOString().slice(0,10),
  is_active: true,
};

export default function Campaigns() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = () => http.get("/campaigns").then((r) => setItems(r.data));
  const tick = useRealtimeTick((m) => m.path.startsWith("/api/campaigns"));
  useEffect(() => { if (tick) load(); }, [tick]); // eslint-disable-line
  useEffect(() => {
    load();
    http.get("/products").then((r) => setProducts(r.data));
  }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => {
    const tiers = (c.tiers && c.tiers.length)
      ? c.tiers.map((t) => ({ threshold_qty: t.threshold_qty, reward_amount: t.reward_amount }))
      : (c.threshold_qty > 0 ? [{ threshold_qty: c.threshold_qty, reward_amount: c.reward_amount }] : [{ threshold_qty: 10, reward_amount: 500 }]);
    setEditing(c);
    setForm({ ...c, product_ids: c.product_ids || [], tiers });
    setOpen(true);
  };

  const addTier = () => {
    const last = form.tiers[form.tiers.length - 1] || { threshold_qty: 0, reward_amount: 0 };
    setForm({ ...form, tiers: [...form.tiers, { threshold_qty: last.threshold_qty * 2 || 10, reward_amount: last.reward_amount * 2 || 500 }] });
  };
  const removeTier = (i) => setForm({ ...form, tiers: form.tiers.filter((_, idx) => idx !== i) });
  const updateTier = (i, key, val) => setForm({
    ...form,
    tiers: form.tiers.map((t, idx) => idx === i ? { ...t, [key]: Number(val) } : t),
  });

  const save = async () => {
    if (!form.name) { toast.error("İsim zorunlu"); return; }
    if (!form.tiers.length) { toast.error("En az bir kademe ekleyin"); return; }
    try {
      const sortedTiers = [...form.tiers].sort((a,b) => a.threshold_qty - b.threshold_qty)
        .map((t) => ({ threshold_qty: Number(t.threshold_qty), reward_amount: Number(t.reward_amount) }));
      const payload = {
        ...form,
        threshold_qty: sortedTiers[0]?.threshold_qty || 0,
        reward_amount: sortedTiers[0]?.reward_amount || 0,
        tiers: sortedTiers,
      };
      if (editing) await http.put(`/campaigns/${editing.id}`, payload);
      else await http.post("/campaigns", payload);
      toast.success("Kaydedildi"); setOpen(false); load();
    } catch { toast.error("Hata"); }
  };

  const toggle = async (c) => {
    await http.patch(`/campaigns/${c.id}/toggle`);
    load();
  };

  const remove = async (c) => {
    if (!window.confirm("Kampanya silinsin mi?")) return;
    await http.delete(`/campaigns/${c.id}`);
    toast.success("Silindi"); load();
  };

  const toggleProduct = (pid) => {
    const list = form.product_ids.includes(pid) ? form.product_ids.filter((x) => x !== pid) : [...form.product_ids, pid];
    setForm({ ...form, product_ids: list });
  };

  return (
    <div className="space-y-6">
      <header className="animate-fade-up flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Kampanyalar</h1>
          <p className="text-slate-400 text-sm mt-1">Eşik ve hakediş kurallarını yönetin</p>
        </div>
        <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-500" data-testid="campaign-new">
          <Plus size={16} className="mr-1" /> Yeni Kampanya
        </Button>
      </header>

      <Card className="border-slate-800 bg-slate-900/60 glass animate-fade-up delay-100">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-slate-400">Kampanya</TableHead>
                <TableHead className="text-slate-400">Kapsam</TableHead>
                <TableHead className="text-slate-400 text-right">Eşik</TableHead>
                <TableHead className="text-slate-400 text-right">Ödül</TableHead>
                <TableHead className="text-slate-400">Tarih</TableHead>
                <TableHead className="text-slate-400">Durum</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id} className="border-slate-800" data-testid={`campaign-row-${c.id}`}>
                  <TableCell>
                    <div className="font-semibold">{c.name}</div>
                    {c.description && <div className="text-xs text-slate-500">{c.description}</div>}
                  </TableCell>
                  <TableCell>
                    <Badge className="bg-slate-800 text-slate-300 border-slate-700">
                      {c.scope === "branch" ? "Şube" : "Personel"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {(c.tiers && c.tiers.length > 0) ? (
                      <div className="text-xs space-y-0.5">
                        {c.tiers.map((t, i) => (
                          <div key={i}><span className="text-slate-400">{t.threshold_qty}+ adet</span></div>
                        ))}
                      </div>
                    ) : (
                      <span>{fmtNum(c.threshold_qty)} adet</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-bold text-amber-300">
                    {(c.tiers && c.tiers.length > 0) ? (
                      <div className="text-xs space-y-0.5">
                        {c.tiers.map((t, i) => (
                          <div key={i}>{fmtNum(t.reward_amount)}₺</div>
                        ))}
                      </div>
                    ) : (
                      <>{fmtNum(c.reward_amount)}₺</>
                    )}
                    <span className="text-xs text-slate-500 block">{c.reward_type === "per_unit" ? "/adet" : "sabit"}</span>
                  </TableCell>
                  <TableCell className="text-xs text-slate-400 font-mono">{c.start_date} → {c.end_date}</TableCell>
                  <TableCell>
                    <Badge className={c.is_active ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-slate-700 text-slate-400"}>
                      {c.is_active ? "Aktif" : "Pasif"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => toggle(c)} data-testid={`campaign-toggle-${c.id}`}><Power size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(c)}><Pencil size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(c)}><Trash2 size={14} className="text-red-400" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10"><Megaphone className="mx-auto mb-2" />Kampanya yok</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Kampanya Düzenle" : "Yeni Kampanya"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>İsim</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="bg-slate-950/50 border-slate-700" data-testid="campaign-form-name" />
            </div>
            <div>
              <Label>Açıklama</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                        className="bg-slate-950/50 border-slate-700" rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kapsam</Label>
                <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                  <SelectTrigger className="bg-slate-950/50 border-slate-700"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="branch">Şube Bazlı</SelectItem>
                    <SelectItem value="employee">Personel Bazlı</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ödül Tipi</Label>
                <Select value={form.reward_type} onValueChange={(v) => setForm({ ...form, reward_type: v })}>
                  <SelectTrigger className="bg-slate-950/50 border-slate-700"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Sabit Tutar</SelectItem>
                    <SelectItem value="per_unit">Adet Başına</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Kademeler (Eşik + Ödül)</Label>
                <Button type="button" size="sm" variant="outline" className="h-7 border-slate-700" onClick={addTier} data-testid="tier-add">
                  <Plus size={12} className="mr-1" /> Kademe Ekle
                </Button>
              </div>
              <div className="space-y-2">
                {form.tiers.map((t, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end" data-testid={`tier-row-${i}`}>
                    <div>
                      {i === 0 && <Label className="text-xs text-slate-500">Eşik Adet</Label>}
                      <Input type="number" value={t.threshold_qty} onChange={(e) => updateTier(i, "threshold_qty", e.target.value)}
                             className="bg-slate-950/50 border-slate-700" data-testid={`tier-threshold-${i}`} />
                    </div>
                    <div>
                      {i === 0 && <Label className="text-xs text-slate-500">Ödül (₺)</Label>}
                      <Input type="number" value={t.reward_amount} onChange={(e) => updateTier(i, "reward_amount", e.target.value)}
                             className="bg-slate-950/50 border-slate-700" data-testid={`tier-reward-${i}`} />
                    </div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeTier(i)} className="text-red-400" data-testid={`tier-remove-${i}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
                <p className="text-[11px] text-slate-500 italic">
                  Örn: 10 adet → 500₺, 20 adet → 1200₺. En yüksek ulaşılan kademenin ödülü verilir.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Başlangıç</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                       className="bg-slate-950/50 border-slate-700" />
              </div>
              <div>
                <Label>Bitiş</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                       className="bg-slate-950/50 border-slate-700" />
              </div>
            </div>
            <div>
              <Label>Dahil Ürünler ({form.product_ids.length} seçili - boşsa tümü)</Label>
              <div className="mt-1.5 max-h-40 overflow-y-auto grid grid-cols-2 gap-1 border border-slate-700 rounded-xl p-2 bg-slate-950/50">
                {products.map((p) => (
                  <button type="button" key={p.id} onClick={() => toggleProduct(p.id)}
                    className={`text-left text-xs px-2 py-1.5 rounded-lg border ${form.product_ids.includes(p.id) ? "bg-blue-500/20 border-blue-500/40 text-blue-200" : "border-slate-800 text-slate-400 hover:bg-slate-800/50"}`}>
                    {p.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label>Aktif</Label>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-700">İptal</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-500" data-testid="campaign-form-save">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
